import { useContext } from 'react'
import type { MediaAsset } from '../../ui'
import {
	assetHubCollectionFilterAtom,
	assetHubOpenAtom,
	assetHubPromiseAtom,
	assetHubSearchAtom,
	assetHubSortAtom,
} from './atoms'
import { AssetHubStoreContext } from './store'

export function useAssetHub() {
	const store = useContext(AssetHubStoreContext)
	if (!store) {
		throw new Error('useAssetHub must be used within an AssetHubManager')
	}

	const open = (): Promise<MediaAsset | undefined> => {
		return new Promise<MediaAsset | undefined>((resolve, reject) => {
			// If a previous promise is pending, reject it
			const prev = store.get(assetHubPromiseAtom)
			if (prev) {
				prev.reject(new DOMException('Asset hub was re-opened by another caller', 'AbortError'))
			}

			// Reset the sidebar filters each time the picker is opened. Users who got
			// lost in a previous session shouldn't have to figure out why they can't
			// find their assets — give them a clean slate.
			store.set(assetHubCollectionFilterAtom, null)
			store.set(assetHubSortAtom, 'date-desc')
			store.set(assetHubSearchAtom, '')

			store.set(assetHubPromiseAtom, { resolve, reject })
			store.set(assetHubOpenAtom, true)
		})
	}

	const close = () => {
		const current = store.get(assetHubPromiseAtom)
		if (current) {
			current.resolve(undefined)
		}
		store.set(assetHubPromiseAtom, null)
		store.set(assetHubOpenAtom, false)
	}

	return { open, close }
}
